/* eslint-disable jsx-a11y/role-supports-aria-props */
/* eslint-disable jsx-a11y/aria-role */
// @flow
import React, { forwardRef } from 'react';
import shortId from 'shortid';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import { ICONS } from 'Components/utils/icon/icons';

import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';
import { DropDown } from 'Components/frameworks/drop-down/DropDown.jsx';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';

import { themes } from './DropDownNav.themes';

type Props = {
  className?: string,
  getTrackingData?: string => TrackingData,
  id?: string,
  items: Array<{
    label: string,
    link: *,
  }>,
  selectedItem?: number,
  theme?: string,
};

const ariaButtonId = `DropDownNavButton_${shortId.generate()}`;
const ariaMenuId = `DropDownNavMenu_${shortId.generate()}`;

// We use `forwardRef` to give the parent an access to the <nav> DOM and get its height for example.
// $FlowFixMe
export const DropDownNav = forwardRef((props: Props, ref) => {
  const { className, id, items = [], selectedItem = 0, theme = 'default', getTrackingData } = props;

  if (isEmpty(items) || !Array.isArray(items)) {
    return null;
  }

  const {
    thDropDown,
    thDropDownTheme,
    thIcon,
    thIconWidth,
    thItemLink,
    thItemLinkSelected,
    thItemsList,
    thTrigger,
  } = themes[theme];

  return (
    <nav className={className} data-testid="dropdown-nav" id={id || null} ref={ref}>
      <DropDown
        className={thDropDown}
        hasCloseOnClick
        renderTrigger={(handler, isExpanded) => (
          <button
            aria-controls={ariaMenuId}
            aria-expanded={isExpanded}
            aria-haspopup
            className={thTrigger}
            id={ariaMenuId}
            onClick={handler}
            type="button"
          >
            {items[selectedItem] ? items[selectedItem].label : <div className="loadingDots" />}
            <Icon
              className={thIcon}
              name={ICONS.SELECT}
              width={thIconWidth}
              rotation={isExpanded ? 180 : 0}
            />
          </button>
        )}
        theme={thDropDownTheme}
      >
        {() => (
          <ul id={ariaMenuId} aria-labelledby={ariaButtonId} role="menu" className={thItemsList}>
            {items.filter(({ label }) => label).map(({ label, link }, index) => (
              <li key={label} role="none">
                <TrackClick
                  as={DynamicLink}
                  className={classnames(thItemLink, {
                    [thItemLinkSelected]: index === selectedItem,
                  })}
                  data-testid={`DropDownNav-item-link-${index}`}
                  {...link}
                  trackingData={getTrackingData ? getTrackingData(label) : null}
                  role="menuitem"
                >
                  {label}
                </TrackClick>
              </li>
            ))}
          </ul>
        )}
      </DropDown>
    </nav>
  );
});
